<template>
  <div class="bg-white p-6 rounded-lg shadow-xl">
    <h2 class="text-xl font-semibold text-brand-primary mb-4 border-b pb-2">Your Submitted Issues</h2>
    
    <div v-if="loading" class="text-center py-4">
      <p class="text-gray-500">Loading submitted issues...</p>
    </div>
    <div v-else-if="error" class="text-center py-4">
      <p class="text-red-500">{{ error }}</p>
    </div>
    <div v-else-if="issueReports.length === 0" class="text-center py-4">
      <p class="text-gray-500">You have not submitted any issues yet.</p>
    </div>

    <div v-else class="space-y-3 max-h-96 overflow-y-auto">
      <router-link 
        v-for="report in issueReports" 
        :key="report.id"
        :to="{ name: 'AdminIssueReportDetail', params: { reportId: report.id } }"
        class="block p-3 rounded-md bg-gray-50 border hover:bg-gray-100 transition-colors duration-150"
      >
        <div class="flex items-start justify-between">
          <div>
            <div class="flex items-center space-x-3">
               <p class="text-xs text-gray-500">
                  Reported on {{ formatDate(report.createdAt) }}
                </p>
            </div>
            <p class="mt-2 text-sm text-gray-700">{{ report.initialReport }}</p>
          </div>
          
          <div class="flex items-center flex-shrink-0 ml-4">
             <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize" :class="statusClass(report.status)">
              {{ report.status.replace('-', ' ') }}
            </span>
          </div>
        </div>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useAuth } from '@/composables/useAuth';

const { claims } = useAuth();
const loading = ref(true);
const error = ref(null);
const issueReports = ref([]);

let unsubscribe = null;

const formatDate = (timestamp) => {
  if (!timestamp || !timestamp.seconds) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const statusClass = (status) => {
  switch (status) {
    case 'new':
      return 'bg-red-100 text-red-800';
    case 'in-progress':
      return 'bg-yellow-100 text-yellow-800';
    case 'complete':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const setupListener = (clientId) => {
    const reportsRef = collection(db, 'issueReports');
    const q = query(
      reportsRef, 
      where('clientId', '==', clientId),
      where('isArchived', '==', false),
      orderBy('createdAt', 'desc')
    );

    unsubscribe = onSnapshot(q, (snapshot) => {
      issueReports.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      loading.value = false;
    }, (err) => {
      console.error("Error fetching issue reports:", err);
      error.value = "Failed to load issue reports.";
      loading.value = false;
    });
};

onMounted(() => {
  if (claims.value?.clientId) {
    setupListener(claims.value.clientId);
  } else {
    // Fallback watcher in case claims are not immediately available
    const unwatch = watch(claims, (newClaims) => {
        if (newClaims?.clientId) {
            setupListener(newClaims.clientId);
            unwatch(); // Stop watching once the listener is set up
        }
    }, { immediate: true });

    // Handle cases where claims might never appear
    setTimeout(() => {
        if (loading.value && !unsubscribe) { // Check if listener was set up
            error.value = "Could not verify client ID. Unable to load reports.";
            loading.value = false;
        }
    }, 5000);
  }
});

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});
</script>